import { normalizeSearchText } from '@src/components/Form/DynamicForm/selectFilterOption';
import type {
  OptionList,
  OptionListChunkDTO,
  OptionListDTO,
  OptionListItem,
  OptionListSummary,
} from '@src/models/interfaces/optionList.interface';

export class OptionListMapper {
  static toSummary(dto: OptionListDTO): OptionListSummary {
    return {
      id: dto.id,
      name: dto.name,
      itemCount: dto.itemCount ?? 0,
      archived: dto.archived === true,
      version: dto.version ?? 0,
      divisionId: dto.divisionId ?? null,
    };
  }

  /** Chunks beyond `chunkCount` are leftovers of a shorter version and are ignored. */
  static itemsFromChunks(dto: OptionListDTO, chunks: OptionListChunkDTO[]): OptionListItem[] {
    return chunks
      .filter(chunk => chunk.index < (dto.chunkCount ?? 0))
      .sort((a, b) => a.index - b.index)
      .flatMap(chunk => chunk.items ?? []);
  }

  static toOptionList(dto: OptionListDTO, chunks: OptionListChunkDTO[]): OptionList {
    const items = OptionListMapper.itemsFromChunks(dto, chunks);
    return {
      ...OptionListMapper.toSummary(dto),
      activeItemCount: items.filter(item => item.active).length,
      items,
    };
  }

  /** Trims labels and drops blanks and repeats, ignoring case and accents (first occurrence wins). */
  static uniqueLabels(values: string[]): string[] {
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const value of values) {
      const label = value.trim();
      const key = normalizeSearchText(label);
      if (label && !seen.has(key)) {
        seen.add(key);
        labels.push(label);
      }
    }
    return labels;
  }

  /** Typed or stored labels, one per line: commas, semicolons and tabs belong to the label. */
  static labelsFromLines(text: string): string[] {
    return OptionListMapper.uniqueLabels(text.split(/\r?\n/));
  }

  /** An imported CSV or text file: the first column of each row, quotes stripped. */
  static labelsFromCsv(text: string): string[] {
    return OptionListMapper.uniqueLabels(
      text.split(/\r?\n/).map(
        line => line.match(/^\s*"((?:[^"]|"")*)"/)?.[1]?.replace(/""/g, '"') ?? line.split(/[,;\t]/)[0],
      ),
    );
  }
}
